<template>
  <CCardBody>
    <!-- Filters -->
    <CCard class="filters" accent-color="warning">
      <CCardHeader
        role="button"
        class="text-warning shadow-none card-header"
        @click="resetFilters"
      >
        <strong class="m-0"><CIcon :name="`cil-filter${$store.state.filterShow ? '-x' : ''}`" /> Filters</strong>
        <div class="card-header-actions">
          <CIcon :name="`cil-chevron-${$store.state.filterShow ? 'bottom' : 'top'}`"/>
        </div>
      </CCardHeader>

      <CCollapse :show="$store.state.filterShow">
        <CCardBody class="p-2">
          <div class="row">
            <!-- Country Filter -->
            <div class="col-xl-2 col-sm-6 mb-2 mb-xl-0">
              <v-select
                class="v-select-filter"
                placeholder="Country.."
                :value="$store.state.filters.country"
                :options="authCountries"
                :reduce="c => c.value"
                :searchable="false"
                @input="countryFilter"
              />
            </div>

            <!-- Hub Filter -->
            <div class="col-xl-3 col-sm-6 mb-2 mb-xl-0 pl-xl-0">
              <v-select
                class="v-select-filter"
                placeholder="Hub.."
                label="name"
                v-model="selectedHubs"
                :options="hubFilterOptions"
                :reduce="hub => hub.id"
                :searchable="false"
                multiple
                @input="inputFilter"
              >
                <template v-slot:option="hub">
                  <template v-if="hub.id === -1">
                    <strong>Without Hub</strong>
                  </template>
                  <template v-else>
                    <strong>{{ hub.zip }}</strong> -
                    <span>{{ hub.name }} </span>
                  </template>
                </template>
              </v-select>
            </div>

            <!-- Location Filter -->
            <div class="col-xl-3 col-sm-6 mb-2 mb-xl-0 pl-xl-0">
              <v-select
                class="v-select-filter"
                placeholder="Location.."
                label="zcCity"
                v-model="selectedLocations"
                :filterable="false"
                :options="locations"
                :reduce="zc => ({id: zc.id, zcZip: zc.zcZip, zcCity: zc.zcCity})"
                @search="onSearchLocation"
                @input="inputFilter"
                multiple
              >
                <template v-slot:option="zip_code">
                  <strong>{{ zip_code.zcZip }}</strong> -
                  <span>{{ zip_code.zcCity }} </span>
                </template>
                <template slot="selected-option" slot-scope="zip_code">
                  <strong class="text-dark mr-1">{{ zip_code.zcZip }}</strong>
                  <span>{{ zip_code.zcCity }} </span>
                </template>
                <span slot="no-options">Type to get locations..</span>
              </v-select>
            </div>

            <!-- Distance Threshold Filter -->
            <div class="col-xl-2 col-sm-6 mb-2 mb-xl-0 pl-xl-0">
              <v-select
                class="v-select-filter"
                placeholder="Distance Threshold.."
                :value="hasDistanceThreshold"
                :options="distanceThresholdOptions"
                :reduce="opt => opt.value"
                :searchable="false"
                @input="distanceThresholdFilter"
              />
            </div>

            <!-- Search -->
            <div class="col-xl col-md mb-1 mb-md-0 pl-xl-0">
              <CInput
                class="mb-0 search"
                type="search"
                placeholder="Search for something else ..."
                v-model="search"
                @input="searchFilter"
              />
            </div>
          </div>

          <div class="mt-2">
            <CInputCheckbox
              custom
              class="mb-0"
              label="Show inactive restaurants"
              :checked.sync="includeNoneActive"
              @update:checked="inputFilter"
            />
          </div>
        </CCardBody>
      </CCollapse>
    </CCard>

    <!-- Info Alert for Distance Threshold -->
    <CAlert 
      v-if="hasDistanceThreshold || hasRestaurantsWithThreshold"
      color="info" 
      class="mb-0"
      :show="true"
    >
      <CIcon name="cil-info" class="mr-2" />
      <strong>Distance Threshold Information:</strong>
      <div class="mt-2">
        Restaurants with a distance threshold configured will use Menute couriers for orders within the specified distance (e.g., 1000m). 
        Orders exceeding this distance will be delivered by the restaurant's own couriers instead of Menute delivery service.
      </div>
    </CAlert>

    <CDataTable
      striped
      hover
      :items="loadedItems"
      :fields="fields"
      :sorter="{ external: true, resetable: true }"
      :itemsPerPageSelect="{
        external: true,
        values: [5, 15, 25, 50, 100, 250, 500],
      }"
      :items-per-page.sync="itemsPerPage"
      :loading="loading"
      :noItemsView="{
        noResults: 'No filtering results are available!',
        noItems: 'No restaurants with delivery service found!',
      }"
      @update:sorter-value="sorterValue"
      @pagination-change="paginationChange"
    >
      <!-- Actions -->
      <template #actions="{ item, index }">
        <td>
          <CSpinner v-if="item.deleting" size="sm" color="danger" class="ml-3" />
          <CButton
            v-else
            class="ml-1"
            size="sm"
            color="danger"
            variant="ghost"
            shape="pill"
            @click="deleteItem(item, index)"
          >
            <CIcon name="cil-trash" />
          </CButton>
        </td>
      </template>

      <!-- id -->
      <template #id="{item}">
        <td>
          {{ item.restaurant_id }}
        </td>
      </template>

      <!-- Location -->
      <template #location="{ item }">
        <td>
          <div class="d-flex align-items-center flex-gap-2">
            <CIcon :content="$options.flagSet[item.country.flag]" size="lg" />
            <div>
              <strong>{{item.restaurant.restaurant_city }}</strong> <small>{{item.restaurant.restaurant_postcode }}</small>
            </div>
          </div>
        </td>
      </template>

      <!-- Name -->
      <template #name="{item}">
        <td>
          <strong>
            <CLink :to="{name: 'View Restaurant', params: { id: item.restaurant.id }}" class="text-dark">{{ item.restaurant.restaurant_name }}</CLink>
          </strong>
        </td>
      </template>

      <!-- Commission Type -->
      <template #type_commission_ds="{item}">
        <td class="text-center">
          <strong>
            <CLink :to="{name: 'Edit Restaurant', params: { id: item.restaurant.id }, query: { tab: 7 }}">
              {{ item.restaurant.type_commission_ds }}
            </CLink>
          </strong>
        </td>
      </template>

      <!-- Distance Threshold -->
      <template #distance_threshold="{item}">
        <td class="text-center">
          <CBadge v-if="item.restaurant?.delivery_service_distance_threshold_meter" color="info">
            {{ item.restaurant.delivery_service_distance_threshold_meter }}m
          </CBadge>
          <span v-else class="text-muted">-</span>
        </td>
      </template>

      <!-- Hubs -->
      <template #hubs="{item}">
        <td class="p-0">
          <CIcon v-if="item._classes === 'table-warning'" name="cil-warning" size="lg" />
          <CBadge class="mx-1" color="secondary" v-for="hub in item.hubs" :key="hub.id">{{ hub.name }}</CBadge>
        </td>
      </template>

      <!-- Started -->
      <template #started_at="{item}">
        <td>{{ moment(item.started_at).format('LL') }}</td>
      </template>
    </CDataTable>

    <!-- Actions -->
    <CCard class="actions sticky-bottom">
      <CCardBody class="p-2">
        <div class="d-flex flex-wrap align-items-center" style="gap: 0.75rem">
          <div>
            <h5 class="mt-1">Total: <span class="d-inline count bg-primary pb-1">{{ total }}</span></h5>
          </div>

          <div v-if="pages > 1" class="ml-auto">
            <CPagination
              align="center"
              :dots='false'
              :pages="pages"
              :active-page.sync="activePage"
            />
          </div>
        </div>
      </CCardBody>
    </CCard>

    <mc-spinner :opacity="0.8" v-show="loading" :mtop="(32 + mcSpinnerMarginTop) + 'px'" />
  </CCardBody>
</template>

<script>
import _debounce from 'lodash/debounce';
import { flagSet } from '@coreui/icons'

export default {
  name: "BackendTable",
  flagSet,
  data() {
    return {
      loadedItems: [],
      fields: [
        { key: "actions", label: "", sorter: false, _style: "min-width: 48px; width: 48px;" },
        { key: "id", label: "ID", sorter: false, _style: "min-width: 48px; width: 48px;" },
        { key: "location", label: "Location", sorter: false, _style: "min-width: 180px; width: 180px;" },
        { key: "name", label: "Restaurant Name", _style: "min-width: 150px;" },
        { key: "hubs", label: "Hubs", sorter: false, _style: "min-width: 150px;" },
        { key: "type_commission_ds", label: "Commission", sorter: false, _style: "min-width: 120px; width: 120px; text-align: center; padding-right: 0 !important;" },
        { key: "distance_threshold", label: "Distance Threshold", sorter: false, _style: "min-width: 140px; width: 140px; text-align: center;" },
        { key: "started_at", label: "Started", _style: "min-width: 140px; width: 140px;" },
      ],

      activePage: 1,
      pages: 1,
      total: 0,
      items_per_page: null,
      orderBy: null,
      direction: null,
      search: null,
      hasDistanceThreshold: null,
      selectedHubs: [],
      selectedLocations: [],
      includeNoneActive: false,
      hubs: [],
      locations: [],

      distanceThresholdOptions: [
        { label: 'All', value: null },
        { label: 'With Distance Threshold', value: true },
      ],

      loading: false,

      // Element Cover Spinner
      mcSpinnerMarginTop: 0,
    };
  },

  beforeMount() {
    this.searchFilter = _debounce(() => {
      this.getAllItems();
    }, 1000);
  },

  mounted() {
    this.activePage =  this.queryPage;
    this.getAllItems();
    this.getHubs();

    window.onscroll = () => {
      this.mcSpinnerMarginTop = document.documentElement.scrollTop;
    }
  },

  watch: {
    reloadParams() {
      if(this.queryPage != this.activePage)
        this.$router.push({ name: 'DeliveryService', query: { page: this.activePage } });

      this.onTableChange();
    },
  },

  computed: {
    itemsPerPage: {
      get: function () { return this.items_per_page ? this.items_per_page : parseInt(process.env.VUE_APP_ITEMS_PER_PAGE) },
      set: function (newValue) { this.items_per_page = newValue }
    },
    reloadParams() { return [this.activePage]; },
    queryPage() { return parseInt(this.$route.query.page) || 1; },
    hubFilterOptions() {
      return [
        { id: -1, name: 'Without Hub' },
        ...this.hubs,
      ];
    },
    hasRestaurantsWithThreshold() {
      return this.loadedItems.some(item => 
        item.restaurant?.delivery_service_distance_threshold_meter > 0
      );
    },
  },

  methods: {
    getAllItems() {
      this.loading = true;

      var url = new URL(this.$backend.COURIER.DELIVERY_SERVICE.GET_ALL),
        params = {
          page: this.activePage,
          itemsPerPage: this.itemsPerPage,
          'order-by': this.orderBy,
          direction: this.direction,
          country: this.$store.state.filters.country,
          search: this.search,
          hubs: this.selectedHubs,
          locations: this.selectedLocations.map(z => z.zcZip),
          'include-none-active': this.includeNoneActive,
          'has-distance-threshold': this.hasDistanceThreshold,
        };

      Object.keys(params).forEach((key) => {
        if (
          typeof params[key] !== "undefined" &&
          params[key] !== null &&
          params[key] !== "" &&
          params[key].length !== 0
        )
          url.searchParams.append(key, params[key]);
      });

      this.$axios.get(url)
        .then((response) => {
          return response.data;
        })
        .then((items) => {
          // Son sayfadan daha büyük bir queryPage çağrımı (?page=XXX) yapılırsa;
          if(items.current_page > items.last_page)
            this.activePage = 1;
          else {
            this.activePage = items.current_page;
            this.pages = items.last_page;
            this.total = items.total;

            this.loadedItems = items.data.map((el) => {
              let classes = el.hubs.length ? "" : "table-warning";
              classes += el.restaurant?.none_active == 1 ? " strikeout" : "";

              return {
                ...el,
                country: this.authCountries.find((c) => c.value === el.restaurant?.countryId),
                deleting: false,
                _classes: classes,
              };
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onTableChange() { // Active Page güncellendiğinde çağrılıyor.
      this.getAllItems();
    },
    paginationChange(value) { // Items Per Page güncellendiğinde çağrılıyor.
      this.itemsPerPage = value;
      this.getAllItems();
    },
    sorterValue(item) {
      this.orderBy = item.column;
      this.direction = item.column ? (item.asc ? "asc" : "desc") : null;
      this.getAllItems();
    },
    async getHubs() {
      this.hubs = JSON.parse(localStorage.getItem('hub-list')) || [];

      await this.$axios
        .get(this.$backend.COURIER.HUBS.GET_LIST)
        .then(({ data }) => {
          this.hubs = data;
          localStorage.setItem('hub-list', JSON.stringify(data));
        })
        .catch(() => this.hubs = []);
    },
    async deleteItem(item, index) {
      const confirm = await this.mSwal.fire({
        title: "Are you sure you want to delete?",
        text: "The restaurant will no longer have a delivery service!",
        icon: "warning",
      });

      if (confirm.isConfirmed) {
        const deleteUrl = this.$backend.COURIER.DELIVERY_SERVICE.DELETE.replace("{id}", item.id);
        item.deleting = true;

        try {
          const { data } = await this.$axios.delete(deleteUrl);
          index !== -1 && this.$delete(this.loadedItems, index);
          // item._classes = index !== -1 && 'strikeout disable-item';
          this.$toast.success(data.data.message);
        } catch (error) {
          this.$set(item, 'deleting', false);

          if (error.response) {
            this.$toast.error(`${error.response.data.message}`);
          } else if (error.request) {
            this.$toast.error('No response received from the server.');
          } else {
            this.$toast.error('An unexpected error occurred.');
          }
        } finally {
          this.$forceUpdate();
        }
      }
    },

    // Filters
    countryFilter(value) {
      this.$store.commit("setCountryFilter", value);
      this.selectedLocations = [];
      this.getAllItems();
    },
    onSearchLocation(search, loading) {
      if (search.length) {
        loading(true);
        this.getLocations(loading, search, this);
      }
    },
    getLocations: _debounce((loading, search, vm) => {
      vm.$axios.get(vm.$backend.ZIP_CODES.SEARCH, {
        params: {
          country: vm.$store.state.filters.country,
          fields: 'zcZip,zcCity',
          q: search,
        },
      })
        .then((response) => (vm.locations = response.data))
        .finally(() => { loading(false); });
    }, 1000),
    inputFilter() {
      this.getAllItems();
    },
    distanceThresholdFilter(value) {
      this.hasDistanceThreshold = value;
      this.getAllItems();
    },
    resetFilters() {
      if (this.$store.state.filterShow) {
        this.$store.commit("setCountryFilter", null);
        this.search = "";
        this.hasDistanceThreshold = null;
        this.selectedHubs = [];
        this.selectedLocations = [];
        this.includeNoneActive = false;
        this.getAllItems();
      }
      this.$store.commit('toggle', 'filterShow');
    },
  },
};
</script>

<style>
.table th, .table td {
  padding: 0.5rem 0.25rem;
  vertical-align: middle;
}

.search input {
  font-size: 1rem !important;
}
</style>
